import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  applyPageSeo,
  applySiteJsonLd,
} from '../lib/seo';

/** App routes that are exactly one path segment and are not listing slugs. */
const SINGLE_SEGMENT_APP_PATHS = new Set([
  'search',
  'find-property',
  'login',
  'register',
  'account',
  'bookings',
  'admin',
  'payfast-checkout',
  'booking',
  'listing',
  'p',
]);

function isListingShortPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length !== 1) return false;
  return !SINGLE_SEGMENT_APP_PATHS.has(segments[0].toLowerCase());
}

export default function LayoutSeo() {
  const { pathname } = useLocation();
  const siteLdApplied = useRef(false);

  useLayoutEffect(() => {
    if (!siteLdApplied.current) {
      applySiteJsonLd();
      siteLdApplied.current = true;
    }
  }, []);

  useLayoutEffect(() => {
    if (pathname.startsWith('/listing/') || pathname.startsWith('/p/')) return;
    if (isListingShortPath(pathname)) return;

    if (pathname.startsWith('/admin')) {
      applyPageSeo({
        title: 'Admin',
        description: 'Leisure Breaks administration.',
        path: pathname,
        noIndex: true,
      });
      return;
    }

    if (
      pathname.startsWith('/booking/') ||
      pathname === '/account' ||
      pathname === '/bookings' ||
      pathname === '/login' ||
      pathname === '/register' ||
      pathname === '/payfast-checkout'
    ) {
      applyPageSeo({
        title:
          pathname === '/login'
            ? 'Sign in'
            : pathname === '/register'
              ? 'Create account'
              : pathname === '/account'
                ? 'Account'
                : pathname === '/bookings'
                  ? 'Bookings'
                  : 'Checkout',
        description: `${SITE_NAME} - guest area.`,
        path: pathname,
        noIndex: true,
      });
      return;
    }

    if (pathname === '/' || pathname === '') {
      applyPageSeo({
        title: `${SITE_NAME} - Premium vacation rentals`,
        description: DEFAULT_DESCRIPTION,
        path: '/',
      });
      return;
    }

    if (pathname === '/search') {
      applyPageSeo({
        title: 'Search holiday rentals',
        description:
          'Search and filter vacation rentals by location, guests, and price across South Africa.',
        path: '/search',
      });
      return;
    }

    if (pathname === '/find-property') {
      applyPageSeo({
        title: 'Find a property on the map',
        description: 'Explore Leisure Breaks vacation rentals on an interactive map.',
        path: '/find-property',
      });
      return;
    }

    applyPageSeo({
      title: SITE_NAME,
      description: DEFAULT_DESCRIPTION,
      path: pathname || '/',
    });
  }, [pathname]);

  return null;
}
